import {DocumentNode, useMutation} from '@apollo/client';

import {CONFIRM_EMAIL, INITIATE_RESET_PASSWORD, LOGIN, REFRESH_TOKEN, RESET_PASSWORD} from 'api/graphql-queries';
import {useAlert} from 'hooks/useAlert';
import {ResponseViewModel} from 'models';
import {
    ConfirmEmailRequest,
    InitiateResetPasswordRequest,
    LoginRequestModel,
    ResetPasswordModel,
    LoginResponesModel,
} from 'models/auth';
import {useCallback, useEffect} from 'react';

export const useLoginApi = () => {
    const [loginMutation, {data: response, loading}] = useMutation<
        {crmLogin: LoginResponesModel},
        {input: LoginRequestModel}
    >(LOGIN);

    return {
        login: loginMutation,
        loginResponse: response?.crmLogin,
        loginLoading: loading,
    };
};

export const useRefreshToeknApi = () => {
    const [refreshTokenMutation, {data: response, loading, error}] = useMutation(REFRESH_TOKEN as DocumentNode);
    const refreshToken = useCallback(() => {
        refreshTokenMutation();
    }, [refreshTokenMutation]);
    return {
        refreshToken,
        response,
        loading,
        error,
    };
};

export const useInitiateResetPasswordApi = () => {
    const {invokeSuccessMessage, invokeAlert} = useAlert();
    const [initiateResetPassword, {data, loading, error}] = useMutation<
        {initiateResetPassword: ResponseViewModel<boolean>},
        InitiateResetPasswordRequest
    >(INITIATE_RESET_PASSWORD);

    useEffect(() => {
        if (!loading && data) {
            invokeSuccessMessage('Password reset email has been sent to your email address.');
        }
    }, [data, loading]);

    useEffect(() => {
        if (error) {
            invokeAlert(error.message);
        }
    }, [error]);

    return {
        initiateResetPassword,
        initiateResetPasswordResponse: data?.initiateResetPassword,
        initiateResetPasswordLoading: loading,
    };
};

export const useResetPasswordApi = () => {
    const {invokeSuccessMessage, invokeAlert} = useAlert();
    const [resetPassword, {data, loading, error}] = useMutation<
        {resetPassword: ResponseViewModel<boolean>},
        {input: ResetPasswordModel}
    >(RESET_PASSWORD);

    useEffect(() => {
        if (!loading && data) {
            invokeSuccessMessage('Password has been successfully reset.');
        }
    }, [data, loading]);

    useEffect(() => {
        if (error) {
            invokeAlert(error.message);
        }
    }, [error]);
    return {
        resetPassword,
        resetPasswordResponse: data,
        resetPasswordLoading: loading,
    };
};

export const useConfirmEmailApi = () => {
    const {invokeSuccessMessage, invokeAlert} = useAlert();
    const [confirmEmail, {data, loading, error}] = useMutation<
        {confirmEmail: ResponseViewModel<boolean>},
        ConfirmEmailRequest
    >(CONFIRM_EMAIL);

    useEffect(() => {
        if (!loading && data) {
            invokeSuccessMessage('Password has been successfully set.');
        }
    }, [data, loading]);

    useEffect(() => {
        if (error) {
            invokeAlert(error.message);
        }
    }, [error]);
    return {
        confirmEmail,
        confirmEmailResponse: data?.confirmEmail,
        confirmEmailLoading: loading,
    };
};
